// Notifications outside the page: push notifications through the service worker (public/sw.js), which reach an
// installed admin app or a browser even when the admin panel is closed; and plain browser notifications as a
// fallback where push is not available (development builds, or a browser without the Push API).
import { getPushKey, removePushSubscription, savePushSubscription } from "../services/alerts";

export const notificationsSupported = () => typeof window !== "undefined" && "Notification" in window;
export const pushSupported = () => notificationsSupported() && "serviceWorker" in navigator && "PushManager" in window && process.env.NODE_ENV === "production";
export const notificationPermission = () => (notificationsSupported() ? Notification.permission : "denied");

// The Push API wants the server's public key as bytes
function keyBytes(base64) {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

// This browser's push subscription, if it has one
export async function currentPushSubscription() {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return registration ? registration.pushManager.getSubscription() : null;
}

// Subscribes this browser and tells the server (again, in case another user signed in on it). Returns true when push
// notifications are on.
export async function enablePush() {
  if (!pushSupported() || Notification.permission !== "granted") return false;
  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(await getPushKey()) });
  }
  await savePushSubscription(subscription.toJSON());
  return true;
}

// Signing in: asks for permission right in the click (browsers only ask after one), so new orders can ring in another
// tab or with the panel closed. The panel then turns push on once signed in (permissionAnswer).
// Someone who said no (closed the browser's question, chose Later, or blocked notifications) is asked again only a day
// later. A browser never shows its question again once notifications are blocked, so then the panel explains how to
// allow them in the site settings instead (GlobalOrderAlarm).
const ASKED_KEY = "ownezzy_notify_asked_at";
export const ASK_AGAIN_AFTER = 24 * 60 * 60 * 1000;
const lastAsked = () => {
  try { return Number(localStorage.getItem(ASKED_KEY)) || 0; } catch { return 0; }
};
export const markAsked = () => {
  try { localStorage.setItem(ASKED_KEY, String(Date.now())); } catch { /* asked again on the next visit */ }
};
export const askDue = () => Date.now() - lastAsked() >= ASK_AGAIN_AFTER;

let asked = null;
export function askOnSignIn() {
  if (!notificationsSupported() || Notification.permission !== "default" || asked || !askDue()) return;
  markAsked();
  asked = Promise.resolve(Notification.requestPermission()).catch(() => Notification.permission);
}
export const permissionAnswer = () => (asked || Promise.resolve()).then(() => notificationPermission());

// Asks for permission (must follow a click), then turns push on where possible. Returns the permission.
export async function turnOnNotifications() {
  if (!notificationsSupported()) return "denied";
  if (Notification.permission === "default") markAsked();
  const permission = Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
  if (permission === "granted") await enablePush().catch(() => false);
  return permission;
}

// Signing out: this browser stops getting the user's notifications
export async function disablePush() {
  const subscription = await currentPushSubscription().catch(() => null);
  if (!subscription) return;
  await removePushSubscription(subscription.endpoint).catch(() => {});
  await subscription.unsubscribe().catch(() => {});
}
